import { useMemo, memo, useCallback } from 'react';
import {
  S600,
  E600,
  W500,
  G500,
  G600,
  W400,
  Table,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  TypographyWithTooltip,
  IMaterialTableOptions,
  Typography,
  getTimeFromNow,
  SecurityScoreIcon,
} from '@uniqkey-frontend/shared-app';
import {
  EmployeeAccountStatus,
  EmployeeAccountType,
  GetEmployeeAccountsResponseModel,
} from '@uniqkey-backend-partner/api-client';
import { useNavigate } from 'react-router-dom';
import { isNumber } from 'lodash';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import {
  EMPLOYEE_ACCOUNT_TYPES_TRANSLATION_KEYS,
  SCIM_TRANSLATION_KEYS,
  EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS,
} from '../../../constants';
import {
  TGetOrganizationEmployeeAccountsMethod,
} from '../../../hooks/useEmployeeAccountsAPI/interfaces';

export interface IOrganizationEmployeesTableRow extends GetEmployeeAccountsResponseModel {
  lastActivity: string | null;
}

interface IOrganizationEmployeesTableProps extends
  Omit<IUseTableReturn<IOrganizationEmployeesTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<
    IUseQueryTableFetchReturn<TGetOrganizationEmployeeAccountsMethod>,
    'data' | 'resetQuery' | 'isFetchedAfterMount'
  >
{
  employees: IUseQueryTableFetchReturn<TGetOrganizationEmployeeAccountsMethod>['data'];
  selectedEmployees: IUseTableReturn<IOrganizationEmployeesTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  organizationId: string;
  partnerId?: string;
  groupId?: string;
}

const TABLE_OPTIONS: IMaterialTableOptions<IOrganizationEmployeesTableRow> = {
  selection: false,
};

const STATUS_COLORS = {
  [EmployeeAccountStatus.Active]: S600,
  [EmployeeAccountStatus.Invited]: G600,
  [EmployeeAccountStatus.Archived]: E600,
  [EmployeeAccountStatus.Unprocessed]: G500,
  [EmployeeAccountStatus.Staged]: G600,
  [EmployeeAccountStatus.MigrationInvited]: G600,
  [EmployeeAccountStatus.Migrated]: G600,
  [EmployeeAccountStatus.ExistingUnprocessed]: G500,
};
const SHOW_ROLE_COLUMN = {
  [EmployeeAccountStatus.Active]: true,
  [EmployeeAccountStatus.Invited]: true,
  [EmployeeAccountStatus.Archived]: true,
  [EmployeeAccountStatus.Unprocessed]: false,
  [EmployeeAccountStatus.Staged]: true,
  [EmployeeAccountStatus.MigrationInvited]: true,
  [EmployeeAccountStatus.Migrated]: true,
  [EmployeeAccountStatus.ExistingUnprocessed]: false,
};
const SCIM_COLORS = {
  true: W500,
  false: G600,
};

const TOOLTIP_PROPS = { cursorPointer: true };

const OrganizationEmployeesTable = (props: IOrganizationEmployeesTableProps) => {
  const {
    employees: preEmployees,
    isLoading,
    total,
    localization,
    organizationId,
    partnerId,
    groupId,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedEmployees,
    onRowSelectionChange,
    createColumns,
  } = props;

  const { t, currentLanguage } = useTranslations();
  const navigate = useNavigate();

  const employees = useMemo(() => preEmployees.map((employee) => ({
    ...employee,
    lastActivity: getTimeFromNow({
      date: employee.activityAt,
      locale: currentLanguage,
    }),
  })), [preEmployees, currentLanguage]);

  const handleViewEmployee = useCallback((rowData: IOrganizationEmployeesTableRow) => {
    if (partnerId) {
      navigate(
        `${
          PageRouteEnum.Partners
        }/${partnerId}/${organizationId}/${rowData.employeeAccountId}`,
      );
      return;
    }
    if (groupId) {
      navigate(
        `${
          PageRouteEnum.Groups
        }/${groupId}/${organizationId}/${rowData.employeeAccountId}`,
      );
      return;
    }
    navigate(`${PageRouteEnum.Employee}/${organizationId}/${rowData.employeeAccountId}`);
  }, [navigate, organizationId, partnerId, groupId]);

  const columns = useMemo(() => createColumns([
    {
      title: t('organizationPage.employeesTab.status'),
      field: 'employeeAccountStatus',
      render: (rowData) => (
        <Typography variant="body2" color={STATUS_COLORS[rowData.employeeAccountStatus]}>
          {t(EMPLOYEE_ACCOUNT_STATUS_TRANSLATION_KEYS[rowData.employeeAccountStatus])}
        </Typography>
      ),
      width: '15%',
    },
    {
      title: t('organizationPage.employeesTab.email'),
      field: 'email',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.email}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationPage.employeesTab.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationPage.employeesTab.role'),
      field: 'employeeAccountType',
      render: (rowData) => SHOW_ROLE_COLUMN[rowData.employeeAccountStatus] && (
        <TypographyWithTooltip
          variant="body2"
          color={rowData.employeeAccountType === EmployeeAccountType.KeylessAdmin ? W400 : G600}
        >
          {t(EMPLOYEE_ACCOUNT_TYPES_TRANSLATION_KEYS[rowData.employeeAccountType])}
        </TypographyWithTooltip>
      ),
      width: '10%',
    },
    {
      title: (
        <TypographyWithTooltip
          variant="subtitle1"
          tooltipProps={TOOLTIP_PROPS}
          lineClamp={5}
          title={t('organizationPage.employeesTab.securityScore.columnTooltip')}
        >
          {t('organizationPage.employeesTab.securityScore')}
        </TypographyWithTooltip>
      ),
      field: 'securityScore',
      render: (rowData) => (isNumber(rowData.securityScore)
        ? <SecurityScoreIcon percentage={Math.floor(rowData.securityScore)} />
        : t('common.noScore')),
      width: '15%',
      sorting: false,
    },
    {
      title: t(SCIM_TRANSLATION_KEYS.true),
      field: 'isByScim',
      render: (rowData) => (
        <Typography
          variant="body2"
          color={SCIM_COLORS[rowData.isByScim.toString() as keyof typeof SCIM_COLORS]}
        >
          {t(SCIM_TRANSLATION_KEYS[
            rowData.isByScim.toString() as keyof typeof SCIM_TRANSLATION_KEYS
          ])}
        </Typography>
      ),
      width: '10%',
    },
    {
      title: t('organizationPage.employeesTab.lastActivity'),
      field: 'activityAt',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.lastActivity}
        </TypographyWithTooltip>
      ),
      width: '13%',
    },
    {
      render: (rowData) => {
        if (rowData.employeeAccountStatus === EmployeeAccountStatus.Unprocessed
          || rowData.employeeAccountStatus === EmployeeAccountStatus.ExistingUnprocessed) {
          return (
            <Typography
              variant="subtitle1"
              disabled
            >
              {t('organizationPage.employeesTab.view')}
            </Typography>
          );
        }

        return (
          <Typography
            variant="subtitle1"
            asLink
            onClick={() => handleViewEmployee(rowData)}
          >
            {t('organizationPage.employeesTab.view')}
          </Typography>
        );
      },
      width: '7%',
      sorting: false,
    },
  ]), [createColumns, handleViewEmployee, t]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={employees}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedEmployees}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      rowSelectByKey="employeeAccountId"
      rowHeight={51}
    />
  );
};

export default memo(OrganizationEmployeesTable);
